import { InMemoryDbService } from 'angular-in-memory-web-api';
import { Injectable } from '@angular/core';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import {
  MOCK_1ST_PARTY_DATA_PERSONAL_TRANSACTIONAL,
  MOCK_AUDIENCES,
  MOCK_SOURCES,
  MOCK_STIRISTA_DATA_AUTOMOTIVE_ATTRIBUTES,
  MOCK_STIRISTA_DATA_PERSONAL_ATTRIBUTES,
  MOCK_CUSTOM_AUDIENCES_DATA,
  MOCK_USERS,
  MOCK_CLIENTS,
  CLIENT_DATA_SOURCES,
  CLIENT_MISC_PERMISSIONS,
  MOCK_USER_ROLES,
  MOCK_EXPORTS,
  MOCK_ORGS,
  MOCK_FIELDS,
  MOCK_REPORTS_STATISTICS,
  MOCK_WEBSITE_DATA_VIG_ATTRIBUTES,
  MOCK_EXPORT_DESTINATION_OPTIONS,
  MOCK_WEBSITE_ANALYTICS_REPORT,
  MOCK_EXPORT_DETAIL,
  ONBOARDING_STAGES,
} from '@app/services/in-memory-data/mock-data';
import { environment } from '@env/environment';
import { analytics_report_type } from '@shared/strings/common.strings';

@Injectable({
  providedIn: 'root',
})
export class InMemHeroService implements InMemoryDbService {
  createDb() {
    // To use the BE in parallel, do not enter a live route in here
    // Use bellow get/post/put/patch/delete methods to handle the routes
    const not_a_used_Route = { message: 'Not a used Route' };
    return { not_a_used_Route };
  }

  // Handle custom responses for specific endpoints
  get(reqInfo: any) {
    // Skip if not a mock API request
    if (!reqInfo.url.includes(environment.apiMockUrl)) return undefined;

    if (reqInfo.collectionName === 'audiences' && !reqInfo.id) {
      const audiences = {
        audiences: MOCK_AUDIENCES,
        pagination: {
          page: 1,
          pageSize: 10,
          totalItems: MOCK_AUDIENCES.length,
          totalPages: Math.ceil(MOCK_AUDIENCES.length / 10),
          sortBy: 'updated_at',
          sortOrder: 'desc',
        },
      };

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: audiences,
          status: 200,
        });
      });
    }

    // Handle individual audience requests (GET /audiences/{id})
    if (reqInfo.collectionName === 'audiences' && reqInfo.id) {
      const mockAudienceResponse = MOCK_AUDIENCES.find(
        audience => audience.id === reqInfo.id,
      );

      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAudienceResponse,
          status: 200,
        };
      });
    }

    // Handle the audiences list endpoint (GET /audiences)
    // Note: This block is unreachable due to the earlier check above
    if (reqInfo.collectionName === 'audiences') {
      const mockResponse = {
        audiences: MOCK_AUDIENCES,
        pagination: {
          page: 1,
          pageSize: 10,
          totalItems: MOCK_AUDIENCES.length,
          totalPages: Math.ceil(MOCK_AUDIENCES.length / 10),
          sortBy: 'updated_at',
          sortOrder: 'desc',
        },
      };

      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockResponse,
          status: 200,
        };
      });
    }

    if (
      reqInfo.url &&
      reqInfo.url.match(/\/tenants\/[^\/]+\/allowed-sources-and-categories/)
    ) {
      const mockSourcesResponse = MOCK_SOURCES;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockSourcesResponse,
          status: 200,
        };
      });
    }

    // Attributes mock
    if (reqInfo.url && reqInfo.url.match('categories/8/attributes')) {
      const mockAttributesResponse = MOCK_STIRISTA_DATA_AUTOMOTIVE_ATTRIBUTES;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }
    if (reqInfo.url && reqInfo.url.match('categories/2/attributes')) {
      const mockAttributesResponse = MOCK_STIRISTA_DATA_PERSONAL_ATTRIBUTES;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }

    if (reqInfo.url && reqInfo.url.match('categories/15/attributes')) {
      const mockAttributesResponse = MOCK_1ST_PARTY_DATA_PERSONAL_TRANSACTIONAL;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }
    if (reqInfo.url && reqInfo.url.match('categories/18/attributes')) {
      const mockAttributesResponse = MOCK_WEBSITE_DATA_VIG_ATTRIBUTES;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }

    // Custom Audiences - category 16
    // Match: sources/Custom%20Audiences/categories/16/audiences
    if (
      reqInfo.url &&
      reqInfo.url.match(/sources\/.*\/categories\/16\/audiences/)
    ) {
      const mockAudiencesResponse = MOCK_CUSTOM_AUDIENCES_DATA;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAudiencesResponse,
          status: 200,
        };
      });
    }

    if (
      reqInfo.url &&
      reqInfo.url.match(/^api\/categories\/\d+\/attributes\?tenantID=\d+$/)
    ) {
      const mockAttributesResponse = MOCK_STIRISTA_DATA_PERSONAL_ATTRIBUTES;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }

    if (reqInfo.url && reqInfo.url.match('categories/15/attributes')) {
      const mockAttributesResponse = MOCK_1ST_PARTY_DATA_PERSONAL_TRANSACTIONAL;
      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockAttributesResponse,
          status: 200,
        };
      });
    }

    if (/tenants\/[^\/]+\/users(\?.*)?$/.test(reqInfo.url)) {
      // GET all users (with optional query params)

      const mockUsersResponse = {
        ...MOCK_USERS,
        pagination: {
          ...MOCK_USERS.pagination,
          page: reqInfo.req.params.get('page'),
        },
      };

      return reqInfo.utils.createResponse$(() => ({
        body: mockUsersResponse,
        status: 200,
      }));
    }

    if (/tenants\/[^\/]+\/users\/[^\/]+$/.test(reqInfo.url)) {
      // GET single user by ID
      const id = reqInfo.url.split('/').pop();
      const user = MOCK_USERS.users.find(u => u.id === id);

      return reqInfo.utils.createResponse$(() => ({
        body: user ? { user } : { error: 'User not found' },
        status: user ? 200 : 404,
      }));
    }

    if (
      reqInfo.url &&
      reqInfo.url.match('/organizations/[^\/]+/all-configurations')
    ) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            sourcesAndCategories: CLIENT_DATA_SOURCES,
            misc: CLIENT_MISC_PERMISSIONS,
            onboarding: ONBOARDING_STAGES,
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/organizations/[^\/]+/tenants')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            clients: [
              {
                id: '6943a6bbaa117bca831d493a',
                tenantID: 'd86ee4b0-de3d-4021-b3bf-ec3427694f6c',
                name: 'Mocked Tenant',
                createdAt: '2025-12-18T07:01:15Z',
                updatedAt: '2025-12-18T07:01:15Z',
                totalUsers: 1,
                config: {
                  sourcesAndCategories: [
                    {
                      id: 'Website Data',
                      categories: [
                        {
                          id: 11,
                        },
                        {
                          id: 12,
                        },
                        {
                          id: 10,
                        },
                        {
                          id: 9,
                        },
                        {
                          id: 18,
                        },
                      ],
                    },
                    {
                      id: 'Email Data',
                      categories: [
                        {
                          id: 14,
                        },
                        {
                          id: 13,
                        },
                      ],
                    },
                    {
                      id: '1st Party Data',
                      categories: [
                        {
                          id: 17,
                        },
                        {
                          id: 15,
                        },
                      ],
                    },
                    {
                      id: 'Stirista Data',
                      categories: [
                        {
                          id: 2,
                        },
                        {
                          id: 4,
                        },
                        {
                          id: 1,
                        },
                        {
                          id: 3,
                        },
                        {
                          id: 5,
                        },
                        {
                          id: 6,
                        },
                        {
                          id: 8,
                        },
                        {
                          id: 7,
                        },
                      ],
                    },
                  ],
                  misc: {
                    allowUserManagement: null,
                    emailAnalytics: {
                      id: '550e8400-e29b-41d4-a716-446655440002',
                      label: 'Email Analytics',
                    },
                    emailDashboard: null,
                    websiteAnalytics: {
                      id: '550e8400-e29b-41d4-a716-446655440005',
                      label: 'Website Analytics',
                    },
                    allowDataExports: {
                      id: '550e8400-e29b-41d4-a716-446655440001',
                      label: 'Allow Data Exports',
                    },
                  },
                },
              },
            ],
            pagination: {
              page: 1,
              pageSize: 10,
              totalItems: 1,
              totalPages: 1,
              sortBy: 'created_at',
              sortOrder: 'desc',
            },
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/tenants/[^\/]+/configuration')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            organizationName: 'Mocked Org',
            tenantName: 'Mocked Tenant',
            misc: CLIENT_MISC_PERMISSIONS,
          },
          status: 200,
        });
      });
    }

    // Check for specific roles endpoint BEFORE general tenants endpoint
    if (reqInfo.url && reqInfo.url.match(/\/tenants\/[^\/]+\/roles$/)) {
      const mockRolesResponse = MOCK_USER_ROLES;
      return reqInfo.utils.createResponse$(() => ({
        body: mockRolesResponse,
        status: 200,
      }));
    }

    if (reqInfo.url && reqInfo.url.match('/tenants')) {
      const page = reqInfo.req.params.get('page');
      const pageSize = reqInfo.req.params.get('pageSize');

      const mockResponse = {
        clients: MOCK_CLIENTS.slice(pageSize * (page - 1), pageSize * page),
        pagination: {
          page,
          pageSize,
          totalItems: MOCK_CLIENTS.length,
          totalPages: MOCK_CLIENTS.length / reqInfo.req.params.get('pageSize'),
          sortBy: reqInfo.req.params.get('sortBy'),
          sortOrder: reqInfo.req.params.get('sortOrder'),
        },
      };

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: mockResponse,
          status: 200,
        });
      });
    }
    // Handle single export detail: GET /exports/{exportId}?tenantID=...
    if (reqInfo.url && /\/exports\/[^\/\?]+/.test(reqInfo.url)) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: { export: MOCK_EXPORT_DETAIL },
          status: 200,
        });
      });
    }

    // Handle exports list endpoint
    if (reqInfo.url && reqInfo.url.match('/exports')) {
      const mockResponse = {
        exports: MOCK_EXPORTS.exports,
        pagination: MOCK_EXPORTS.pagination,
      };

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: mockResponse,
          status: 200,
        });
      });
    }

    // Handle export destinations endpoint
    if (reqInfo.url && reqInfo.url.match('/export-destinations')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: MOCK_EXPORT_DESTINATION_OPTIONS,
          status: 200,
        });
      });
    }

    // Handle reports/statistics endpoint
    if (reqInfo.url && reqInfo.url.match('/reports/statistics')) {
      const mockResponse = MOCK_REPORTS_STATISTICS;
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: mockResponse,
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.includes('/report/check-exists')) {
      const reportNameParam = reqInfo.req.params.get('report_name') ?? '';
      const reportTypeParam = reqInfo.req.params.get('report_type');
      const normalizedReportName = reportNameParam.trim().toLowerCase();
      const isSupportedReportType =
        reportTypeParam === analytics_report_type.emailReportId ||
        reportTypeParam === analytics_report_type.websiteReportId;
      const reportIdPrefix =
        reportTypeParam === analytics_report_type.emailReportId
          ? 'mock-email-report-'
          : 'mock-website-report-';

      const report_ids =
        isSupportedReportType && normalizedReportName === 'duplicate-report'
          ? [
              `${isSupportedReportType ? reportIdPrefix : ''}duplicate-report-id`,
            ]
          : [];

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: { report_ids },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/organizations')) {
      const page = reqInfo.req.params.get('page');
      const pageSize = reqInfo.req.params.get('pageSize');

      const mockResponse = {
        organizations: MOCK_ORGS.slice(pageSize * (page - 1), pageSize * page),
        pagination: {
          page,
          pageSize,
          totalItems: MOCK_ORGS.length,
          totalPages: MOCK_ORGS.length / reqInfo.req.params.get('pageSize'),
          sortBy: reqInfo.req.params.get('sortBy'),
          sortOrder: reqInfo.req.params.get('sortOrder'),
        },
      };

      if (reqInfo.url && reqInfo.url.match('/exports/fields')) {
        const mockResponse = {
          categories: MOCK_FIELDS.categories,
          fields: MOCK_FIELDS.fields,
        };
        return reqInfo.utils.createResponse$(() => {
          return new HttpResponse({
            body: mockResponse,
            status: 200,
          });
        });
      }

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: mockResponse,
          status: 200,
        });
      });
    }

    return reqInfo.utils.createResponse$(() => {
      return new HttpErrorResponse({
        error: {
          message: 'Bad API request. Please check the URL and try again.',
        },
        status: 404,
      });
    });
  }

  post(reqInfo: any) {
    // Skip if not a mock API request
    if (!reqInfo.url.includes(environment.apiMockUrl)) return undefined;

    // Create organization
    if (reqInfo.url && reqInfo.url.match('/organizations')) {
      MOCK_ORGS.unshift({
        id: MOCK_ORGS.length + 1,
        createdAt: '2024-01-15',
        updatedAt: '2024-09-10',
        ...reqInfo.req.body,
      });
      const mockResponse = {
        id: MOCK_ORGS.length + 1,
        ...reqInfo.req.body,
      };

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: mockResponse,
          status: 200,
        });
      });
    }

    // Create tenant - TODO should include an organization id in url
    if (reqInfo.url && reqInfo.url.match('/tenants')) {
      MOCK_CLIENTS.unshift({
        id: MOCK_CLIENTS.length + 1,
        createdAt: '2024-01-15',
        updatedAt: '2024-09-10',
        totalUsers: 25,
        ...reqInfo.req.body,
      });
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            id: MOCK_CLIENTS.length + 1,
            ...reqInfo.req.body,
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url === `${environment.apiMockUrl}audience/size`) {
      const mockResponse = { size: 54626 };

      return reqInfo.utils.createResponse$(() => {
        return {
          body: mockResponse,
          status: 200,
        };
      });
    }

    // Handle audience size refresh (POST /audience/refresh-size)
    if (reqInfo.url === `${environment.apiMockUrl}audience/refresh-size`) {
      const audienceIds = reqInfo.req.body?.audiencesIds || [];
      const audiencesSize = audienceIds.map((id: string) => {
        // Find the audience in MOCK_AUDIENCES
        const audience = MOCK_AUDIENCES.find(aud => aud.id === id);

        if (audience) {
          // Generate a slightly varied size (simulate recalculation)
          const variance = Math.floor(Math.random() * 20000) - 10000; // +/- 10k
          const newSize = Math.max(0, audience.audienceSize + variance);

          // Update the mock data
          audience.audienceSize = newSize;
          audience.sizeUpdatedAt = new Date().toISOString();

          return {
            audienceId: id,
            sizeUpdatedAt: audience.sizeUpdatedAt,
            audienceSize: newSize,
          };
        }

        // If audience not found, return a default response
        return {
          audienceId: id,
          sizeUpdatedAt: new Date().toISOString(),
          audienceSize: 0,
        };
      });

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: { audiencesSize },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('website-analytics')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: MOCK_WEBSITE_ANALYTICS_REPORT,
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.includes('/report')) {
      const reportType = reqInfo.req.params.get('report_type');
      const reportIdPrefix =
        reportType === analytics_report_type.emailReportId
          ? 'mock-email-report'
          : reportType === analytics_report_type.websiteReportId
            ? 'mock-website-report'
            : null;

      if (!reportIdPrefix) {
        return reqInfo.utils.createResponse$(
          () =>
            new HttpErrorResponse({
              error: {
                message: `Unsupported or missing report_type query param: ${reportType}`,
              },
              status: 400,
            }),
        );
      }

      return reqInfo.utils.createResponse$(
        () =>
          new HttpResponse({
            body: { report_id: `${reportIdPrefix}-${Date.now()}` },
            status: 200,
          }),
      );
    }

    if (reqInfo.url && reqInfo.url.match(/\/tenants\/[^\/]+\/users(\?.*)?$/)) {
      // Generate a unique ID by finding the highest existing ID and incrementing
      const existingIds = MOCK_USERS.users
        .map(user => parseInt(user.id))
        .filter(id => !isNaN(id));
      const nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;

      const newUser = {
        ...reqInfo.req.body,
        id: nextId.toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'Pending',
        tenantName: 'LG Electronics', //TO DO: Make tenant name dynamic based on a request after tenantId
      };

      // Add the new user to the MOCK_USERS array
      MOCK_USERS.users.push(newUser);

      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            message: 'User invited successfully',
            user: newUser,
          },
          status: 200,
        });
      });
    }
    if (reqInfo.url && reqInfo.url.includes('auth/token')) {
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            id_token:
              'eyJhbGciOiJSUzI1NiIsImtpZCI6IkVFMDI3MjQzQjk5QjhDNkY1RERBRTI1Qzc3ODA4MjZCIiwidHlwIjoiSldUIn0.eyJpc3MiOiJodHRwczovL3Nzb2JldGEuc3RpcmlzdGEuY29tIiwibmJmIjoxNzYwOTY0NjczLCJpYXQiOjE3NjA5NjQ2NzMsImV4cCI6MTc2MDk2NDk3MywiYXVkIjoic3B5Z2xhc3MiLCJhbXIiOlsiZXh0ZXJuYWwiXSwibm9uY2UiOiI2YWIzMTE0MDY5ODRiZThkMDAwNTc5MTY2ZGEyNmQ5MDBlUVFMY0U3YyIsImF0X2hhc2giOiJidzgwNTdNMXlfajROZU1ZeUwybzl3Iiwic2lkIjoiQTEzREQ2QzIwREMwQjIwODM2RDE5OEEzQUMyOTA2NjkiLCJzdWIiOiI2OGU4ZTU1YmEyN2M4OGY3NmM5YjdjNDYiLCJhdXRoX3RpbWUiOjE3NjA5NjQ2NDQsImlkcCI6ImFjY2Vzc0lkZW50aXR5QmV0YSIsIm5hbWUiOiJEYW5pZWwgRnVydHVuYSIsInByb2ZpbGVJZCI6IjY4ZThlNTViYTI3Yzg4Zjc2YzliN2M0NiIsImVtYWlsIjoiZGFuaWVsLmZ1cnR1bmFAYXNzaXN0LnJvIiwib3JnSWQiOiI2OGU4ZTA2MGEyN2M4OGY3NmM5YjdiZGUiLCJ0ZW5hbnRJZCI6IjY4ZThlMDY5YTI3Yzg4Zjc2YzliN2JlMiJ9.SnARUuxZxrky-E9sWY72j47TVs_7Um_s-LK6ryKnHx-vzGLBm81tDBJsY6kUpQqMGfrh5M7FKnNAvBh2q1R4C9gGrTsN19GlKZRZJwWzy6SHI6wY1WR-NGhx9JUKE4x5bQK0GQehhdtJTrpv-2BLFKSd5saZ0RwKmKZJUHkeZz-SBUNJdeJd0H_MNpb-gqQwy8C47TGSEDOcwVPR2dhyDMeXbz9uAIkfF3EJqBUQiZtDVgDW-oR5Mnr3gVPxvM1Mc474s_Sgciq5RAGl74wSQguXJSpP1-_Vof0pmC_X0fgyZAPQtuIm2L5LArkPHY3h8BUEzF2MgouvyMVwLXgbDA',
            access_token:
              'eyJhbGciOiJSUzI1NiIsImtpZCI6IkVFMDI3MjQzQjk5QjhDNkY1RERBRTI1Qzc3ODA4MjZCIiwidHlwIjoiYXQrand0In0.eyJpc3MiOiJodHRwczovL3Nzb2JldGEuc3RpcmlzdGEuY29tIiwibmJmIjoxNzYwOTY0NjcyLCJpYXQiOjE3NjA5NjQ2NzIsImV4cCI6MTc2MDk2ODI3Miwic2NvcGUiOlsib3BlbmlkIiwiYWNjZXNzSWRlbnRpdHkiLCJvZmZsaW5lX2FjY2VzcyJdLCJhbXIiOlsiZXh0ZXJuYWwiXSwiY2xpZW50X2lkIjoic3B5Z2xhc3MiLCJzdWIiOiI2OGU4ZTU1YmEyN2M4OGY3NmM5YjdjNDYiLCJhdXRoX3RpbWUiOjE3NjA5NjQ2NDQsImlkcCI6ImFjY2Vzc0lkZW50aXR5QmV0YSIsIm5hbWUiOiJEYW5pZWwgRnVydHVuYSIsInByb2ZpbGVJZCI6IjY4ZThlNTViYTI3Yzg4Zjc2YzliN2M0NiIsImVtYWlsIjoiZGFuaWVsLmZ1cnR1bmFAYXNzaXN0LnJvIiwib3JnSWQiOiI2OGU4ZTA2MGEyN2M4OGY3NmM5YjdiZGUiLCJ0ZW5hbnRJZCI6IjY4ZThlMDY5YTI3Yzg4Zjc2YzliN2JlMiIsInNpZCI6IkExM0RENkMyMERDMEIyMDgzNkQxOThBM0FDMjkwNjY5IiwianRpIjoiQzRDQkRFQkFDOUIxMTc1Q0E5QzQ3MkI4QjZGMUU4RjcifQ.cih59WZJrtzviApPmX1xm3mCSwXnQewFskGPyE-856xzwYzul14TzjmSh_o-X2zKLs5SOnQ8E_HruY4xz-V0nqidpvCUYE0gYIvLcOqIqrJ4_UnV3N78FpZfmmr7z6pyWxVbaNzRa9VytAWUb3etwpK_FLB-atD2FmFtBcE3Rzb-676H3_JIU0KZTPwC8_Ux70Z5tnZ0IXw46Fwgp0ZVgbvVB9hLTeQj3OSrC_4yWZL5hHDFZVWooQv8lhO_nynOC65CkzL5B_0bFC5grJVe5wCgD7PHNyxb_7Mwbi4MjmVgpqm6FZTP5k3y6A3qQ9EjNkafKWKSs-cYzJV9mZqLHA',
            expires_in: 3600,
            token_type: 'Bearer',
            refresh_token:
              'C77151DDEB5E3A02BEFA94A1F0F27D4495925E0A7F9ADC13C2E47CFE3EF1EDFD-1',
            scope: 'openid accessIdentity offline_access',
          },
          status: 200,
        });
      });
    }

    // Mock activation endpoint
    if (reqInfo.url && reqInfo.url.includes('auth/activate-user')) {
      return reqInfo.utils.createResponse$(() => {
        const requestBody = reqInfo.req.body;
        const userId = requestBody?.ssoUserId;

        // Simulate different responses based on token
        if (!userId) {
          return new HttpResponse({
            body: {
              id: '',
              status: 'error',
            },
            status: 400,
          });
        }

        // Simulate invalid token
        if (userId === 'invalid_userId') {
          return new HttpResponse({
            body: {
              id: '',
              status: 'error',
            },
            status: 400,
          });
        }

        // Simulate successful activation
        return new HttpResponse({
          body: {
            id: '68e8e55ba27c88f76c9b7c46',
            status: 'active',
          },
          status: 200,
        });
      });
    }

    return reqInfo.utils.createResponse$(() => {
      return new HttpErrorResponse({
        error: {
          message: 'Bad API request. Please check the URL and try again.',
        },
        status: 404,
      });
    });
  }

  put(reqInfo: any) {
    // Skip if not a mock API request
    if (!reqInfo.url.includes(environment.apiMockUrl)) return undefined;

    // Handle user update (PUT /tenants/{tenantId}/users/{id})
    if (reqInfo.url && reqInfo.url.match(/\/tenants\/[^\/]+\/users\/[^\/]+$/)) {
      const urlParts = reqInfo.url.split('/');
      const userId = urlParts[urlParts.length - 1];

      return reqInfo.utils.createResponse$(() => {
        const userIndex = MOCK_USERS.users.findIndex(
          user => user.id === userId,
        );

        if (userIndex === -1) {
          return new HttpErrorResponse({
            error: { message: `User with id ${userId} not found` },
            status: 404,
          });
        }

        // Update user with new data
        const updatedUser = {
          ...MOCK_USERS.users[userIndex],
          ...reqInfo.req.body,
          updatedAt: new Date().toISOString(),
        };

        MOCK_USERS.users[userIndex] = updatedUser;

        return new HttpResponse({
          body: updatedUser,
          status: 200,
        });
      });
    }

    return reqInfo.utils.createResponse$(() => {
      return new HttpErrorResponse({
        error: {
          message: 'Bad API request. Please check the URL and try again.',
        },
        status: 404,
      });
    });
  }

  patch(reqInfo: any) {
    // Skip if not a mock API request
    if (!reqInfo.url.includes(environment.apiMockUrl)) return undefined;

    if (reqInfo.url && reqInfo.url.match('/tenants')) {
      const tenantId = reqInfo.url.split('/').slice(-1)[0];
      MOCK_CLIENTS.forEach((client, index) => {
        if (client.id.toString() === tenantId) {
          MOCK_CLIENTS[index] = {
            ...MOCK_CLIENTS[index],
            ...reqInfo.req.body,
          };
        }
      });
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            id: tenantId,
            ...reqInfo.req.body,
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/organizations')) {
      const orgId = reqInfo.url.split('/').slice(-1)[0];
      MOCK_ORGS.forEach((client, index) => {
        if (client.id.toString() === orgId) {
          MOCK_ORGS[index] = {
            ...MOCK_ORGS[index],
            ...reqInfo.req.body,
          };
        }
      });
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            id: orgId,
            ...reqInfo.req.body,
          },
          status: 200,
        });
      });
    }

    return reqInfo.utils.createResponse$(() => {
      return new HttpErrorResponse({
        error: {
          message: 'Bad API request. Please check the URL and try again.',
        },
        status: 404,
      });
    });
  }

  delete(reqInfo: any) {
    // Skip if not a mock API request
    if (!reqInfo.url.includes(environment.apiMockUrl)) return undefined;

    if (reqInfo.url && reqInfo.url.match('/tenants')) {
      const tenantId = reqInfo.url.split('/').slice(-1)[0];

      const indexToDelete = MOCK_CLIENTS.findIndex(
        client => client.id.toString() === tenantId,
      );
      if (indexToDelete !== -1) {
        MOCK_CLIENTS.splice(indexToDelete, 1);
      }
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            id: tenantId,
            ...reqInfo.req.body,
          },
          status: 200,
        });
      });
    }

    if (reqInfo.url && reqInfo.url.match('/organizations')) {
      const orgId = reqInfo.url.split('/').slice(-1)[0];

      const indexToDelete = MOCK_ORGS.findIndex(
        org => org.id.toString() === orgId,
      );
      if (indexToDelete !== -1) {
        MOCK_ORGS.splice(indexToDelete, 1);
      }
      return reqInfo.utils.createResponse$(() => {
        return new HttpResponse({
          body: {
            id: orgId,
            ...reqInfo.req.body,
          },
          status: 200,
        });
      });
    }

    // Handle audience deletion (DELETE /audiences/{id})
    if (reqInfo.collectionName === 'audiences' && reqInfo.id) {
      const audienceToDelete = MOCK_AUDIENCES.find(
        audience => audience.id === reqInfo.id,
      );

      if (!audienceToDelete) {
        return reqInfo.utils.createResponse$(() => {
          return {
            body: { error: 'Audience not found' },
            status: 404,
          };
        });
      }

      return reqInfo.utils.createResponse$(() => {
        return {
          body: {
            message: 'Audience deleted successfully',
            deletedId: reqInfo.id,
          },
          status: 200,
        };
      });
    }

    // Handle other DELETE endpoints if needed
    if (reqInfo.url && reqInfo.url.includes('audiences/')) {
      return reqInfo.utils.createResponse$(() => {
        return {
          body: { message: 'Audience deleted successfully' },
          status: 200,
        };
      });
    }

    // Handle user deletion (DELETE /tenants/{tenantId}/users/{id})
    if (reqInfo.url && reqInfo.url.match(/\/tenants\/[^\/]+\/users\/[^\/]+$/)) {
      const urlParts = reqInfo.url.split('/');
      const userId = urlParts[urlParts.length - 1];

      return reqInfo.utils.createResponse$(() => {
        const userIndex = MOCK_USERS.users.findIndex(
          user => user.id === userId,
        );

        if (userIndex === -1) {
          return {
            body: { error: `User with id ${userId} not found` },
            status: 404,
          };
        }

        // Remove user from MOCK_USERS array
        MOCK_USERS.users.splice(userIndex, 1);

        return {
          body: { message: 'User deleted successfully' },
          status: 200,
        };
      });
    }

    return reqInfo.utils.createResponse$(() => {
      return new HttpErrorResponse({
        error: {
          message: 'Bad API request. Please check the URL and try again.',
        },
        status: 404,
      });
    });
  }
}
